import { Mail, Phone, Facebook, Instagram, Twitter, Linkedin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Footer = () => {
  const { toast } = useToast();

  const copyEmail = () => {
    navigator.clipboard.writeText('evan@vincentgrowthpartners.co.uk');
    toast({
      title: "Email copied!",
      description: "evan@vincentgrowthpartners.co.uk has been copied to your clipboard.",
    });
  };

  const copyPhoneNumber = () => {
    navigator.clipboard.writeText('+447857450763');
    toast({
      title: "Phone number copied!",
      description: "+447857450763 is in your clipboard. Shall we start your growth journey?",
      duration: 5000,
    });
  };

  return (
    <footer className="bg-primary text-primary-foreground py-12">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div className="md:col-span-1">
            <h3 className="text-2xl font-bold mb-2 text-accent">Vincent Growth Partners</h3>
            <p className="text-primary-foreground/70 text-sm mb-4">See what we're up to & what clients say</p>
            {/* 2x3 Social Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div 
                className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center hover:bg-accent/30 transition-colors cursor-pointer"
                onClick={copyEmail}
                title="Copy email address"
              >
                <Mail className="w-5 h-5 text-accent" />
              </div>
              <a 
                href="https://www.linkedin.com/company/vincent-growth-partners/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center hover:bg-accent/30 transition-colors"
                title="Connect with us on LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-accent" />
              </a>
              <a 
                href="https://www.instagram.com/vincentgrowthpartners/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center hover:bg-accent/30 transition-colors"
                title="Follow us on Instagram"
              >
                <Instagram className="w-5 h-5 text-accent" />
              </a>
              <a 
                href="https://x.com/GrowthVgp"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center hover:bg-accent/30 transition-colors"
                title="Follow us on X"
              >
                <Twitter className="w-5 h-5 text-accent" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61578173273716"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center hover:bg-accent/30 transition-colors"
                title="Follow us on Facebook"
              >
                <Facebook className="w-5 h-5 text-accent" />
              </a>
              <a 
                href="https://uk.trustpilot.com/review/vincentgrowthpartners.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#00b67a]/20 rounded-full flex items-center justify-center hover:bg-[#00b67a]/30 transition-colors"
                title="Read our Trustpilot reviews"
              >
                <svg className="w-5 h-5 text-[#00b67a]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0L15.09 7.26L23 8.27L17.5 13.14L18.82 21.02L12 17.27L5.18 21.02L6.5 13.14L1 8.27L8.91 7.26L12 0Z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Contact */}
          <div className="md:col-span-1">
            <h4 className="text-lg font-semibold mb-4 text-primary-foreground">Get In Touch</h4>
            <div className="space-y-3 text-primary-foreground/80">
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-accent flex-shrink-0" />
                <div>
                  <div 
                    onClick={copyPhoneNumber}
                    className="font-semibold hover:text-accent transition-colors cursor-pointer text-sm"
                    title="Click to copy phone number"
                  >
                    07857 450763
                  </div>
                  <p className="text-xs text-primary-foreground/60">AI Reception - 24/7 Available</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-accent flex-shrink-0" />
                <span 
                  className="cursor-pointer hover:text-accent transition-colors text-sm break-all"
                  onClick={copyEmail}
                  title="Click to copy email address"
                >
                  evan@vincentgrowthpartners.co.uk
                </span>
              </div>
              <div className="pt-3">
                <a 
                  href="/do-you-qualify"
                  className="inline-block w-full px-4 py-3 bg-accent text-primary rounded-lg hover:bg-accent/90 transition-colors font-semibold text-sm text-center"
                >
                  Schedule Strategic Review
                </a>
              </div>
            </div>
          </div>

          {/* Grow With Us - LinkedIn Newsletter */}
          <div className="md:col-span-1">
            <h4 className="text-lg font-semibold mb-4 text-primary-foreground">Grow With Us</h4>
            <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
              <p className="text-accent font-semibold text-sm mb-2">📰 Weekly Growth Tips</p>
              <p className="text-primary-foreground/70 text-xs mb-3">Actionable marketing strategies delivered to your feed</p>
              <a 
                href="https://www.linkedin.com/build-relation/newsletter-follow?entityUrn=7376990429063577600"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full px-4 py-2 rounded-lg text-white bg-[#0A66C2] hover:bg-[#094c93] transition-colors text-sm font-medium"
              >
                Subscribe on LinkedIn
              </a>
            </div>
          </div>
        </div>


        <div className="border-t border-primary-foreground/10 pt-6 text-center text-primary-foreground/60 space-y-2">
          <p className="text-xs">Vincent Growth Partners Ltd | Company No. 16166010 | Registered in England & Wales</p>
          <p className="text-xs">Registered Address: Hubbard Way, 2 Civic Drive, Ipswich, Suffolk, IP1 2QA</p>
          <p>&copy; 2025 Vincent Growth Partners Ltd. All rights reserved. | <a href="/privacy-policy" className="hover:text-accent transition-colors">Privacy Policy</a> | <a href="/content-policy" className="hover:text-accent transition-colors">Content Policy</a> | <a href="/terms-of-service" className="hover:text-accent transition-colors">Terms of Service</a></p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;